// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { jsonrepair } from 'jsonrepair';
import { sliceToStructure } from './balance';

/**
 * The paste pipeline, in this order and no other (RESEARCH §6):
 *
 *   1. first-party balance scan — REFUSE a truncated paste rather than repair it
 *   2. slice to the outermost balanced span ourselves, stripping prose and code fences
 *   3. only now call jsonrepair
 *   4. always show the diff
 *
 * Steps 1 and 2 exist because jsonrepair does its job on object literals and does the wrong thing on
 * everything either side of one: it throws on any prose prefix, silently turns "prose, then a fence"
 * into a JSON array of prose strings, and silently completes a truncated belt into a plausible,
 * fully-formed, wrong one.
 */

export interface ParsedPaste {
  readonly value: unknown;
  readonly original: string;
  readonly repaired: string;
  readonly changed: boolean;
  readonly notes: readonly string[];
}

export function parsePaste(text: string): ParsedPaste {
  const { json, notes } = sliceToStructure(text);
  const all = [...notes];

  try {
    return { value: JSON.parse(json), original: json, repaired: json, changed: false, notes: all };
  } catch {
    // Not valid JSON. It is very likely a Python dict, a TS literal, or has trailing commas.
  }

  let repaired: string;
  try {
    repaired = jsonrepair(json);
  } catch (e) {
    throw new Error(
      `This does not parse as JSON, and could not be repaired: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  let value: unknown;
  try {
    value = JSON.parse(repaired);
  } catch (e) {
    throw new Error(
      `Repair produced something that still does not parse: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  all.push('The paste was not valid JSON and was repaired — check the diff before trusting the result.');
  return { value, original: json, repaired, changed: true, notes: all };
}

// ── A minimal line diff, purely for showing the repair ──────────────────────

export type DiffOp = { readonly kind: ' ' | '-' | '+'; readonly text: string };

/**
 * Line-level LCS. Small on purpose: this renders one repair of one paste, never a large corpus, and
 * a dependency for it would be a third library in a bundle that ships two.
 */
export function lineDiff(a: string, b: string): DiffOp[] {
  const A = a.split('\n');
  const B = b.split('\n');
  const n = A.length;
  const m = B.length;

  // Guard: an enormous paste would make the O(n·m) table costly, and the diff is cosmetic.
  if (n * m > 4_000_000) {
    return [
      { kind: '-', text: `${n} line${n === 1 ? '' : 's'} in, ${m} out — too large to diff line by line.` },
    ];
  }

  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = A[i] === B[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const out: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) {
      out.push({ kind: ' ', text: A[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ kind: '-', text: A[i]! });
      i++;
    } else {
      out.push({ kind: '+', text: B[j]! });
      j++;
    }
  }
  while (i < n) out.push({ kind: '-', text: A[i++]! });
  while (j < m) out.push({ kind: '+', text: B[j++]! });
  return out;
}

/** Collapse long runs of unchanged lines so the diff panel stays readable. */
export function compactDiff(ops: readonly DiffOp[], context = 2): DiffOp[] {
  const keep = new Array<boolean>(ops.length).fill(false);
  ops.forEach((op, i) => {
    if (op.kind === ' ') return;
    for (let k = Math.max(0, i - context); k <= Math.min(ops.length - 1, i + context); k++) keep[k] = true;
  });
  const out: DiffOp[] = [];
  let skipped = 0;
  ops.forEach((op, i) => {
    if (keep[i]) {
      if (skipped > 0) {
        out.push({ kind: ' ', text: `… ${skipped} unchanged line${skipped === 1 ? '' : 's'} …` });
        skipped = 0;
      }
      out.push(op);
    } else {
      skipped++;
    }
  });
  if (skipped > 0) out.push({ kind: ' ', text: `… ${skipped} unchanged line${skipped === 1 ? '' : 's'} …` });
  return out;
}
